// src/pages/Gallery.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { galleryItems } from '../models/portfolioData';

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 }),
};

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const [paused, setPaused] = useState(false);

  const categories = useMemo(() => {
    const cats = ['All'];
    galleryItems.forEach((item) => {
      if (item.category && !cats.includes(item.category)) {
        cats.push(item.category);
      }
    });
    return cats;
  }, []);

  const filteredItems = useMemo(() => {
    if (activeCategory === 'All') return galleryItems;
    return galleryItems.filter((item) => item.category === activeCategory);
  }, [activeCategory]);

  const total = filteredItems.length;
  const safeIndex = current >= total ? 0 : current;
  const item = filteredItems[safeIndex];

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  const goPrev = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + total) % total);
  };

  const goTo = (index) => {
    setDirection(index > safeIndex ? 1 : -1);
    setCurrent(index);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrent(0);
    setDirection(1);
  };

  useEffect(() => {
    if (paused || total <= 1) return;
    const timer = setInterval(goNext, 4500);
    return () => clearInterval(timer);
  }, [paused, goNext, total]);

  return (
    <section id="gallery" className="section bg-gray-50 dark:bg-[#0f0f17]">
      <div className="container text-center">
        <p className="section-subtitle">Moments & Activities</p>
        <h6 className="section-title mb-6">Gallery</h6>

        {/* Category Filter Tabs */}
        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {categories.map((cat) => {
              const count =
                cat === 'All'
                  ? galleryItems.length
                  : galleryItems.filter((i) => i.category === cat).length;
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`capitalize transition-all duration-200 text-sm ${
                    isActive
                      ? 'btn btn-primary btn-sm rounded shadow-sm'
                      : 'btn btn-outline-primary btn-sm rounded'
                  }`}
                >
                  {cat} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Carousel */}
        <div className="max-w-4xl mx-auto">
          <div
            className="relative overflow-hidden rounded-xl border border-gray-200 dark:border-[#272738] shadow-md bg-black"
            style={{ minHeight: 380, height: 460 }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={`${activeCategory}-${safeIndex}`}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.45, ease: 'easeInOut' }}
                className="absolute inset-0"
              >
                <img
                  src={item?.image}
                  alt={item?.title}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-6 sm:p-8 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    {item?.category && (
                      <span className="font-heading bg-primary text-white px-3 py-0.5 rounded-full text-xs font-semibold shadow-sm">
                        {item.category}
                      </span>
                    )}
                    <span className="text-white/80 bg-black/50 backdrop-blur-sm px-2.5 py-0.5 rounded-full text-xs font-mono">
                      {safeIndex + 1} / {total}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg sm:text-2xl text-white mb-2 leading-tight">
                    {item?.title}
                  </h3>
                  <p className="text-gray-200 text-xs sm:text-sm max-w-2xl leading-relaxed">
                    {item?.summary}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Prev / Next buttons */}
            {total > 1 && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 dark:bg-black/60 text-gray-800 dark:text-white flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-200 shadow"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/80 dark:bg-black/60 text-gray-800 dark:text-white flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-200 shadow"
                  aria-label="Next image"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
          </div>

          {/* Interactive Thumbnails */}
          {total > 1 && (
            <div className="flex justify-center items-center gap-2 sm:gap-3 mt-4 overflow-x-auto py-2 px-1 max-w-full">
              {filteredItems.map((it, idx) => (
                <button
                  key={it.id}
                  onClick={() => goTo(idx)}
                  className={`relative flex-shrink-0 w-14 h-10 sm:w-16 sm:h-12 rounded-md overflow-hidden border-2 transition-all duration-200 ${
                    idx === safeIndex
                      ? 'border-primary ring-2 ring-primary/40 scale-105 opacity-100 shadow'
                      : 'border-transparent opacity-60 hover:opacity-100 hover:border-gray-400'
                  }`}
                  aria-label={`Go to slide ${idx + 1}: ${it.title}`}
                >
                  <img
                    src={it.image}
                    alt={it.title}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}

          {/* Dot indicators */}
          {total > 1 && (
            <div className="flex justify-center items-center gap-2 mt-2">
              {filteredItems.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === safeIndex
                      ? 'bg-primary w-6'
                      : 'bg-gray-300 dark:bg-gray-600 hover:bg-primary/50 w-2'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
